import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  ChangeEventHandler
} from 'react';
import {Autocomplete, ListBoxOptionType} from '@flixbus/honeycomb-react';
import fieldInfoContent from '@/Pages/ProductionCost/constants/fieldInfoContent';
import {PartnerNameAutocompleteFieldProps} from './PartnerNameAutocompleteFieldProps.types';
import LabelWithTooltip from '@/components/LabelWithTooltip/LabelWithTooltip';

const PartnerNameAutocompleteField: React.FC<
  PartnerNameAutocompleteFieldProps
> = ({partners, busPartner, vehicleId, errors, onSelectPartnerName}) => {
  const [partnerTitle, setPartnerTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState<ListBoxOptionType[]>(
    []
  );

  const partnerOptions = useMemo<ListBoxOptionType[]>(
    () =>
      partners.map(partner => ({
        title: partner.name,
        value: partner.id
      })),
    [partners]
  );

  const partnerOption = useMemo(() => {
    if (!busPartner?.id) return;
    return partnerOptions.find(partner => partner.value === busPartner.id);
  }, [busPartner?.id, partnerOptions]);

  useEffect(() => {
    setPartnerTitle(partnerOption?.title ?? '');
  }, [partnerOption?.title]);

  const handleDebounce = useCallback(
    async (inputValue: string) => {
      const searchedText = inputValue.toLowerCase();

      if (!searchedText) {
        setFilteredOptions([]);
        setLoading(false);
        return [];
      }

      setLoading(true);
      const filteredPartners = partnerOptions.filter(option =>
        option.title.toLowerCase().startsWith(searchedText.toLowerCase())
      );
      setFilteredOptions(filteredPartners);
      setLoading(false);

      return filteredPartners;
    },
    [partnerOptions]
  );

  const handleChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    e => {
      setLoading(true);
      setPartnerTitle(e.target.value);
    },
    [setLoading, setPartnerTitle]
  );

  const handleSelect = useCallback(
    (selectedOptions: ListBoxOptionType[]) => {
      const selected = selectedOptions[0];
      if (selected) {
        setPartnerTitle(selected.title);
        onSelectPartnerName(selected);
      }
    },
    [onSelectPartnerName]
  );

  const renderOption = useCallback(
    (option: ListBoxOptionType) => option.title,
    []
  );

  const id = `partner-name-for-calculation-${vehicleId}`;

  const label = useMemo(
    () => (
      <LabelWithTooltip
        label="Partner name for calculation*"
        htmlFor={id}
        tooltipProps={{
          id: `tooltip-${id}`,
          content: fieldInfoContent.partnerNameForCalculation,
          position: 'bottom'
        }}
      />
    ),
    [id]
  );

  return (
    <Autocomplete
      comboboxProps={{
        id,
        label,
        placeholder: 'Select a partner name',
        loading,
        onChange: handleChange,
        valid: !errors,
        infoError: errors?.optionality ?? errors?.nullable
      }}
      options={filteredOptions}
      inputValue={partnerTitle}
      listBoxProps={{
        optionsToDisplay: 4,
        optionProps: (option: ListBoxOptionType) => ({
          renderOption: () => renderOption(option)
        })
      }}
      onDebounce={handleDebounce}
      onChange={handleSelect}
    />
  );
};

export default React.memo(PartnerNameAutocompleteField);
